import { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { PostDesignAPI } from "@/features/dmManagement/api/postDesignAPI";
import { extractFileError } from "@/features/dmManagement/utils/extractFileError";
import { useApiClient } from "@/hooks/useApiClient";
import type {
  ProductCategory,
  Product,
  DesignFormData,
} from "@/features/dmManagement/types";

const designFormSchema = z.object({
  taskId: z.preprocess(
    (val) => (val === "" || val === undefined ? undefined : Number(val)),
    z
      .number({
        required_error: "識別番号を入力してください",
        invalid_type_error: "識別番号を入力してください",
      })
      .int("整数を入力してください")
      .positive("正の整数を入力してください")
  ),
  taskName: z
    .string({ required_error: "識別名を入力してください" })
    .min(1, "識別名を入力してください")
    .max(256, "256文字以内で入力してください"),
  categoryId: z.preprocess(
    (val) => (val === "" || val === undefined ? undefined : Number(val)),
    z.number({
      required_error: "商品仕様を選択してください",
    })
  ),
});

type DesignBaseFormValues = z.infer<typeof designFormSchema>;

export interface ComponentField {
  seq: number;
  products: Product[];
  label: string;
}

/** 商品仕様のコンポーネント数に応じた表示ラベルを返す（封筒+封入物の組み合わせで決定） */
const getComponentLabels = (componentCount: number): string[] => {
  if (componentCount === 1) return ["デザインデータ"];
  if (componentCount === 2) return ["封筒デザイン", "封入物"];
  return ["封筒", "封入物1", "封入物2"];
};

interface UseDesignFormReturn {
  form: ReturnType<typeof useForm<DesignBaseFormValues>>;
  isSubmitting: boolean;
  canSubmit: boolean;
  componentFields: ComponentField[];
  componentFiles: Record<number, File>;
  productSelections: Record<number, number>;
  fileErrors: Record<number, string>;
  handleFileChange: (seq: number, file: File | null) => void;
  handleProductChange: (seq: number, productId: number) => void;
  handleFormSubmit: (e: React.FormEvent) => void;
}

/**
 * デザイン登録フォームの状態管理フック。
 *
 * カテゴリ選択に基づくコンポーネントフィールドの動的生成、
 * ファイルアップロード管理、バリデーション、送信処理を管理する。
 */
export const useDesignForm = (
  categories: ProductCategory[]
): UseDesignFormReturn => {
  const { apiClient } = useApiClient();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [componentFiles, setComponentFiles] = useState<Record<number, File>>(
    {}
  );
  const [productSelections, setProductSelections] = useState<
    Record<number, number>
  >({});
  const [fileErrors, setFileErrors] = useState<Record<number, string>>({});

  const form = useForm<DesignBaseFormValues>({
    mode: "all",
    resolver: zodResolver(designFormSchema),
  });

  const selectedCategoryId = form.watch("categoryId");
  const selectedCategory = categories.find(
    (c) => c.id === Number(selectedCategoryId)
  );

  // allowedEnclosingSeqs でグループ化し、封入順（seq）でソートしてコンポーネント入力欄を生成する
  const componentFields: ComponentField[] = useMemo(() => {
    if (!selectedCategory) return [];

    const labels = getComponentLabels(selectedCategory.componentCount);
    const seqMap = new Map<number, Product[]>();

    selectedCategory.products.forEach((product) => {
      product.allowedEnclosingSeqs.forEach((seq) => {
        const existing = seqMap.get(seq);
        if (existing) {
          existing.push(product);
        } else {
          seqMap.set(seq, [product]);
        }
      });
    });

    return Array.from(seqMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([seq, products], index) => ({
        seq,
        products,
        label: labels[index] ?? `コンポーネント${index + 1}`,
      }));
  }, [selectedCategory]);

  useEffect(() => {
    setComponentFiles({});
    setFileErrors({});

    const autoSelections: Record<number, number> = {};
    componentFields.forEach((field) => {
      if (field.products.length === 1) {
        autoSelections[field.seq] = field.products[0].id;
      }
    });

    setProductSelections(autoSelections);
    // selectedCategoryId（プリミティブ値）のみを依存配列にすることで、
    // categories の参照変更による不要なリセットを防ぐ
  }, [selectedCategoryId]);

  const handleFileChange = useCallback(
    (seq: number, file: File | null): void => {
      if (file) {
        setComponentFiles((prev) => ({ ...prev, [seq]: file }));
        setFileErrors((prev) => {
          const { [seq]: _, ...rest } = prev;
          return rest;
        });
      } else {
        setComponentFiles((prev) => {
          const { [seq]: _, ...rest } = prev;
          return rest;
        });
      }
    },
    []
  );

  const handleProductChange = useCallback(
    (seq: number, productId: number): void => {
      setProductSelections((prev) => ({ ...prev, [seq]: productId }));
    },
    []
  );

  // フォームバリデーション通過 + 全コンポーネントにファイル選択済み + 商品選択済み で送信可能
  const canSubmit = useMemo(
    () =>
      form.formState.isValid &&
      componentFields.length > 0 &&
      componentFields.every((f) => componentFiles[f.seq]) &&
      componentFields.every(
        (f) => f.products.length <= 1 || productSelections[f.seq]
      ),
    [form.formState.isValid, componentFields, componentFiles, productSelections]
  );

  const onSubmit = useCallback(
    async (formData: DesignBaseFormValues): Promise<void> => {
      const newFileErrors: Record<number, string> = {};
      let hasError = false;
      componentFields.forEach((field) => {
        if (!componentFiles[field.seq]) {
          newFileErrors[field.seq] = "ファイルを選択してください";
          hasError = true;
        }
      });
      if (hasError) {
        setFileErrors(newFileErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        const designData: DesignFormData = {
          taskId: formData.taskId,
          taskName: formData.taskName,
          categoryId: formData.categoryId,
          components: componentFields.map((field) => ({
            productId: productSelections[field.seq],
            enclosingSeq: field.seq,
            file: componentFiles[field.seq],
          })),
        };

        await PostDesignAPI(apiClient, designData);
        toast.success("デザインデータを登録しました");
        navigate("/dmManagement/designs");
      } catch (error) {
        const message =
          extractFileError(error) ?? "デザインデータの登録に失敗しました";
        toast.error(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [componentFields, componentFiles, productSelections, apiClient, navigate]
  );

  const handleFormSubmit = useCallback(
    (e: React.FormEvent): void => {
      void form.handleSubmit(onSubmit)(e);
    },
    [form, onSubmit]
  );

  return {
    form,
    isSubmitting,
    canSubmit,
    componentFields,
    componentFiles,
    productSelections,
    fileErrors,
    handleFileChange,
    handleProductChange,
    handleFormSubmit,
  };
};
